import { Loading } from 'components'
import { translate } from 'locales'
import { formatLocalCap } from 'utils/date'
import React, { useEffect, useState } from 'react'
import { statusColor } from '../Overview/statusColors'
import { findFinancialMonthHistory } from '../services'
import { IFinancialMonthDetail, IFinancialMonthRow } from '../types'
import {
  formatCurrency,
  formatDate,
  getMonthHistoryStatus,
  getVisibleMonths,
  sumConsultations,
} from '../utils'
import { monthlyHistoryStatusLabelKey } from './statusLabels'
import * as S from './styles'

interface MonthlyHistoryProps {
  locale: string
  onViewDetails: (month: IFinancialMonthRow) => void
}

const MonthlyHistory = ({ locale, onViewDetails }: MonthlyHistoryProps) => {
  const visibleMonths = getVisibleMonths(new Date().getFullYear())

  const [historyLoading, setHistoryLoading] = useState(false)
  const [history, setHistory] = useState<IFinancialMonthDetail[]>([])

  useEffect(() => {
    findFinancialMonthHistory(
      visibleMonths.length,
      setHistory,
      setHistoryLoading
    )
  }, [])

  const rows: IFinancialMonthRow[] = history
    .filter((month) => month.totalValue > 0)
    .sort((a, b) => b.periodMonth.localeCompare(a.periodMonth))
    .map((month) => ({
      ...month,
      status: getMonthHistoryStatus(month),
      consultationsCount: sumConsultations(month.stats),
    }))

  const loading = historyLoading

  return (
    <S.Section>
      {loading && <Loading title={translate('financial.loading')} />}

      <h2>{translate('financial.monthlyHistory.title')}</h2>
      <span className="section-subtitle">
        {translate('financial.monthlyHistory.subtitle')}
      </span>

      <S.TableWrapper>
        <S.Table>
          <thead>
            <tr>
              <th>{translate('financial.monthlyHistory.columnMonth')}</th>
              <th>
                {translate('financial.monthlyHistory.columnConsultations')}
              </th>
              <th>{translate('financial.monthlyHistory.columnTotalValue')}</th>
              <th>{translate('financial.monthlyHistory.columnClosing')}</th>
              <th>{translate('financial.monthlyHistory.columnStatus')}</th>
              <th>{translate('financial.monthlyHistory.columnActions')}</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((month) => (
              <tr key={month.periodMonth}>
                <S.MonthCell>
                  {formatLocalCap(`${month.periodMonth}-01`, 'MMMM yyyy')}
                </S.MonthCell>
                <td>{month.consultationsCount}</td>
                <S.TotalValueCell>
                  {formatCurrency(month.totalValue, locale)}
                </S.TotalValueCell>
                <td>{formatDate(month.closingDate)}</td>
                <td>
                  <S.StatusBadge color={statusColor(month.status)}>
                    <S.Dot color={statusColor(month.status)} />
                    {translate(monthlyHistoryStatusLabelKey(month.status))}
                  </S.StatusBadge>
                </td>
                <td>
                  <S.ViewDetailsButton
                    type="button"
                    onClick={() => onViewDetails(month)}
                  >
                    {translate('financial.monthlyHistory.viewDetails')}
                  </S.ViewDetailsButton>
                </td>
              </tr>
            ))}
          </tbody>
        </S.Table>

        {!loading && rows.length === 0 && (
          <S.EmptyState>
            {translate('financial.monthlyHistory.empty')}
          </S.EmptyState>
        )}
      </S.TableWrapper>
    </S.Section>
  )
}

export default MonthlyHistory
